import axios from '../../../helpers/axios';
import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { QUERY_KEYS } from '../../../queries/query.keys';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { notificationState } from '../../../recoil/notification.atom';
import { ICustomerContact } from '../interfaces/customerContact';

export const useMutateCustomerContact = (customerId: string) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const setNotification = useSetRecoilState(notificationState);

    const create = useMutation<AxiosResponse, AxiosError, ICustomerContact>(
        (data: ICustomerContact) => axios.post(`customer-contact`, data),
        {
            onSuccess: async (res) => {
                setNotification({
                    open: true,
                    severity: 'success',
                    message: t('CUSTOMER_ADDRESSES.CREATE_SUCCESS')
                });
                return res;
            },
            onError: (err) => {
                console.error(err.request.response);
                setNotification({ open: true, severity: 'error', message: t('CUSTOMER_ADDRESSES.CREATE_ERROR') });
            },
            onSettled: async () => {
                await queryClient.invalidateQueries([QUERY_KEYS.CUSTOMER_CONTACT_LIST, customerId]);
            }
        }
    );

    const update = useMutation<AxiosResponse, AxiosError, ICustomerContact>(
        (data: ICustomerContact) => axios.put(`customer-contact/${data.id}`, data),
        {
            onSuccess: async (res) =>
                setNotification({
                    open: true,
                    severity: 'success',
                    message: t('CUSTOMER_ADDRESSES.UPDATE_SUCCESS')
                }),
            onError: (err) => {
                console.error(err.request.response);
                setNotification({ open: true, severity: 'error', message: t('CUSTOMER_ADDRESSES.UPDATE_ERROR') });
            },
            onSettled: async () => {
                await queryClient.invalidateQueries([QUERY_KEYS.CUSTOMER_CONTACT_LIST, customerId]);
            }
        }
    );

    const remove = useMutation<AxiosResponse, AxiosError, ICustomerContact>(
        (data: ICustomerContact) => axios.delete(`customer-contact/${data.id}`),
        {
            onSuccess: async (res) =>
                setNotification({
                    open: true,
                    severity: 'success',
                    message: t('CUSTOMER_ADDRESSES.DELETE_SUCCESS')
                }),
            onError: (err) => {
                console.error(err.request.response);
                setNotification({ open: true, severity: 'error', message: t('CUSTOMER_ADDRESSES.DELETE_ERROR') });
            },
            onSettled: async () => {
                await queryClient.invalidateQueries([QUERY_KEYS.CUSTOMER_CONTACT_LIST, customerId]);
            }
        }
    );

    return { update, create, remove };
};
